import { Pagination, Spinner, Table, TableBody, TableDataCell, TableRow } from '@consigli/facade';
import { useLazyGetProjectsQuery, useMyUser, useUpdateProject } from '@consigli/hooks';
import { Project, ProjectRole } from '@consigli/types';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';

import { DeleteConfirmationPopup } from './components/delete-confirmation-popup';
import {
  ProjectTableHeader,
  SortConfig,
  SortField,
  SortDirection,
} from './components/project-table-header';
import { ProjectTableRow } from './components/project-table-row';

export const ProjectManagementList: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useMyUser();
  const pageSize = 25;
  const [page, setPage] = useState(1);
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSorting, setIsSorting] = useState(false);
  const [paginatedCount, setPaginatedCount] = useState(0);
  const [projectToDelete, setProjectToDelete] = useState<string | null>(null);
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    field: 'created_at',
    direction: 'desc',
  });
  const [getProjects] = useLazyGetProjectsQuery();
  const { updateProject } = useUpdateProject();
  const isInitialLoadRef = useRef(true);

  const adminProjectIds = useMemo(() => {
    if (user.isSuperuser || user.isOrgAdmin) return null;
    return new Set(
      user.projectAccess
        .filter((project) => project.role === ProjectRole.ADMIN)
        .map((project) => project.id),
    );
  }, [user.isSuperuser, user.isOrgAdmin, user.projectAccess]);

  const filterAdminProjects = useCallback(
    (allProjects: Project[]) => {
      if (adminProjectIds === null) return allProjects;
      return allProjects.filter((project) => adminProjectIds.has(project.id));
    },
    [adminProjectIds],
  );

  useEffect(() => {
    const fetchProjects = async () => {
      // For initial load, show full loading state
      // For sorting/pagination, show subtle loading without hiding content
      if (isInitialLoadRef.current) {
        setIsLoading(true);
        isInitialLoadRef.current = false;
      } else {
        setIsSorting(true);
      }
      try {
        // Convert sortConfig to ordering parameter for backend
        const ordering = sortConfig.direction === 'asc' ? sortConfig.field : `-${sortConfig.field}`;

        const result = await getProjects({
          page,
          pageSize,
          isHidden: false,
          ordering,
        }).unwrap();

        const filtered = filterAdminProjects(result.results);
        setProjects(filtered);
        setPaginatedCount(adminProjectIds === null ? result.count : filtered.length);
      } catch (error) {
        console.error('Error fetching projects:', error);
      } finally {
        setIsLoading(false);
        setIsSorting(false);
      }
    };

    fetchProjects();
  }, [getProjects, page, pageSize, sortConfig, filterAdminProjects, adminProjectIds]);

  const handlePageChange = useCallback(
    (page: number) => {
      setPage(page);
    },
    [setPage],
  );

  const handleSort = useCallback(
    (field: SortField, direction: SortDirection) => {
      // Prevent rapid successive sorting clicks
      if (isSorting) return;
      setSortConfig({ field, direction });
      setPage(1); // Reset to first page when sorting
    },
    [setSortConfig, setPage, isSorting],
  );

  const handleProjectClick = useCallback(
    (projectId: string) => {
      navigate(`/projects/${projectId}/services`);
    },
    [navigate],
  );

  const handleDelete = useCallback((e: React.MouseEvent, projectId: string) => {
    e.stopPropagation();
    setProjectToDelete(projectId);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!projectToDelete) return;
    try {
      await updateProject({
        projectId: Number(projectToDelete),
        data: { isHidden: true },
      }).unwrap();
      toast.success(t('project-management.deleted'));

      // Use sorting state for smooth refresh
      setIsSorting(true);
      const ordering = sortConfig.direction === 'asc' ? sortConfig.field : `-${sortConfig.field}`;
      const result = await getProjects({
        page,
        pageSize,
        isHidden: false,
        ordering,
      }).unwrap();
      const filtered = filterAdminProjects(result.results);
      setProjects(filtered);
      setPaginatedCount(adminProjectIds === null ? result.count : filtered.length);
      setIsSorting(false);
    } catch (error) {
      console.log('Error deleting project:', error);
      toast.error(t('project-management.delete-error'));
      setIsSorting(false);
    }
    setProjectToDelete(null);
  }, [
    projectToDelete,
    updateProject,
    t,
    getProjects,
    page,
    pageSize,
    sortConfig,
    filterAdminProjects,
    adminProjectIds,
  ]);

  return (
    <>
      <div className="relative bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm max-h-[calc(100vh-310px)] overflow-y-auto">
        {isSorting && (
          <div className="absolute inset-0 bg-white/60 z-10 flex items-center justify-center">
            <Spinner />
          </div>
        )}
        <Table>
          <ProjectTableHeader onSort={handleSort} sortConfig={sortConfig} isSorting={isSorting} />
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableDataCell colSpan={5} className="py-20">
                  <div className="w-full flex justify-center">
                    <div className="flex flex-col items-center">
                      <Spinner size="medium" />
                      <p className="mt-4 text-day-neutral-subtle font-medium text-center">
                        {t('project-management.loading')}
                      </p>
                    </div>
                  </div>
                </TableDataCell>
              </TableRow>
            ) : projects.length === 0 ? (
              <TableRow>
                <TableDataCell colSpan={5} className="py-12">
                  <div className="w-full flex justify-center">
                    <p className="text-day-neutral-subtle font-medium text-center">
                      {t('project-management.no-projects')}
                    </p>
                  </div>
                </TableDataCell>
              </TableRow>
            ) : (
              projects.map((project, index) => (
                <ProjectTableRow
                  key={project.id}
                  project={project}
                  onDelete={(e) => handleDelete(e, project.id.toString())}
                  onClick={() => handleProjectClick(project.id.toString())}
                  isLastRow={index === projects.length - 1}
                />
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {!isLoading && !isSorting && projects.length > 0 && (
        <div className="mt-4 relative flex items-center justify-center">
          <p className="absolute left-0 font-bold text-sm text-day-neutral-subtle">
            {paginatedCount} {t('project-management.projects')}
          </p>
          <Pagination
            currentPage={page}
            totalCount={paginatedCount}
            pageSize={pageSize}
            onPageChange={handlePageChange}
          />
        </div>
      )}

      <DeleteConfirmationPopup
        isOpen={!!projectToDelete}
        onClose={() => setProjectToDelete(null)}
        onConfirm={handleConfirmDelete}
      />
    </>
  );
};
